/**
 * Phase 5 PR 5.3 — `dbToPlanTable` adapter.
 *
 * Converts a `{ trainingPlan, planDays }` result from `useTrainingPlan`
 * into the `ParsedPlanTable` shape consumed by the existing
 * `<PlanTable />` component (Phase 4 PR 5 + the post-Phase-4 expand-row
 * follow-up). This is the moment the wai-plan-v1 loop closes:
 *
 *   Training Coach → wai-plan-v1 JSON → /api/plans/parse-and-persist
 *                                                 ↓
 *                                  plan_days.expanded_details JSONB
 *                                          (PR 5.0 column)
 *                                                 ↓
 *                                       dbToPlanTable adapter
 *                                                 ↓
 *                                         ParsedPlanTable
 *                                                 ↓
 *                                    PlanTable expand-row chevron
 *
 * Per Phase 5 plan §"Round 2 → 3": for rows with NULL
 * `expanded_details` (legacy plans created before PR 5.0), `expanded`
 * is `null` and PlanTable graceful-degrades — no chevron renders for
 * that row.
 *
 * Pure / SSR-safe / no React. Fully unit-testable.
 */

import type { ParsedPlanTable, PlanRow } from './planTableParser';
import type { PlanExpandedFields, PlanV1 } from './parsePlanV1Json';
import type { PlanDayRow, TrainingPlanRow } from '../hooks/useTrainingPlan';

/**
 * The 6 keys we accept off `plan_days.expanded_details` (PR 5.0 narrow
 * shape). Mirrors `PER_FIELD_MAX_LENGTH` keys in `api/lib/plan-persist.js`.
 * Anything else on the JSONB is silently dropped here.
 */
const ALLOWED_EXPANDED_KEYS: readonly (keyof PlanExpandedFields)[] = [
  'warmup',
  'main_set',
  'cooldown',
  'pace_target',
  'coach_note',
  'recovery_nutrition',
  // EC-2 — engine clinical adjustment note.
  'clinical_note',
];

/**
 * Convert a JSONB blob from `plan_days.expanded_details` into the
 * strictly typed `PlanExpandedFields`. Only string values for the 6
 * documented keys survive — anything else is dropped.
 *
 * Returns null when:
 *   - input is null / undefined / non-object
 *   - no keys remain after filtering
 *
 * The PR 5.0 persist helper already enforces this shape; this filter
 * is defense-in-depth so a malformed legacy row can't poison the
 * render path.
 */
export function expandedDetailsToFields(
  raw: Record<string, unknown> | null | undefined,
): PlanExpandedFields | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: PlanExpandedFields = {};
  let kept = 0;
  for (const key of ALLOWED_EXPANDED_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed.length === 0) continue;
    out[key] = trimmed;
    kept += 1;
  }
  return kept === 0 ? null : out;
}

/**
 * Try to extract a numeric pace string ("8:30/mi") from the row's
 * data, in priority order:
 *   1. expanded.pace_target — most reliable when the LLM emitted it
 *   2. details (regex match) — fallback for legacy plans
 *
 * Returns undefined when nothing parses — PlanTable renders no pace
 * cell content.
 */
export function extractPace(
  expanded: PlanExpandedFields | null,
  details: string | null | undefined,
): string | undefined {
  if (expanded && typeof expanded.pace_target === 'string') {
    // Prefer a numeric "M:SS/unit" snippet when the pace_target is
    // descriptive ("Conversational, 8:30/mi"); fall through to the
    // raw string if the helper finds nothing useful.
    const numericMatch = expanded.pace_target.match(/(\d{1,2}:\d{2})\s*\/\s*(mi|km)/i);
    if (numericMatch) return `${numericMatch[1]}/${numericMatch[2].toLowerCase()}`;
    if (expanded.pace_target.trim().length > 0) return expanded.pace_target.trim();
  }
  if (typeof details === 'string') {
    const m = details.match(/(\d{1,2}:\d{2})\s*\/\s*(mi|km)/i);
    if (m) return `${m[1]}/${m[2].toLowerCase()}`;
  }
  return undefined;
}

/**
 * Day-of-week number (Mon-based 0..6) → 3-letter label matching
 * PlanTable's `today` detection (which checks `Mon`/`Tue`/.../`Sun`).
 *
 * `plan_days.day_label` already carries the same string ("Mon" /
 * "Tue" / etc.) — we use the persisted label primarily, fall back to
 * this map only if a row is missing its label (defensive).
 */
const DOW_TO_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/**
 * Adapt the DB shape `{ trainingPlan, planDays }` into the
 * `ParsedPlanTable` consumed by `<PlanTable />`. Returns null when
 * input is incomplete (no plan or no days).
 *
 * The `start` / `end` / `raw` fields on `ParsedPlanTable` are sentinel
 * values — they're meaningful only when the table came from
 * `parsePlanTable(text)`. From the DB path nothing references them.
 */
export function dbToPlanTable(args: {
  trainingPlan: TrainingPlanRow | null;
  planDays: PlanDayRow[] | null;
}): ParsedPlanTable | null {
  const { trainingPlan, planDays } = args;
  if (!trainingPlan || !planDays || planDays.length === 0) return null;

  const rows: PlanRow[] = planDays
    .slice()
    .sort((a, b) => (a.day_of_week ?? 0) - (b.day_of_week ?? 0))
    .map((pd): PlanRow => {
      const dayLabel =
        (pd.day_label && pd.day_label.trim()) ||
        DOW_TO_LABEL[Math.max(0, Math.min(6, pd.day_of_week ?? 0))];
      const expanded = expandedDetailsToFields(pd.expanded_details);
      const pace = extractPace(expanded, pd.details);
      return {
        day: dayLabel,
        workout: pd.workout_type ?? '',
        details: pd.details ?? '',
        ...(pace !== undefined ? { pace } : {}),
        // PR 5.3: `expanded` is the load-bearing field that lights up
        // the chevron in PlanTable. NULL = no chevron, graceful
        // degradation for pre-PR-5.0 legacy rows.
        expanded,
        // B-19 — carry the stored actual raw onto the row model (additive,
        // null-safe). PlanTable pairs it with the parsed planned mileage at
        // render, gated by the b19_actuals_v1 flag; a row with no actual (or the
        // flag off) renders exactly as today.
        actualDistance: pd.actual_distance ?? null,
        actualDistanceUnit: pd.actual_distance_unit ?? null,
      };
    });

  return {
    start: 0,
    end: 0,
    raw: '',
    rows,
    weekHeader: trainingPlan.title || `Week ${trainingPlan.week_number}`,
    ...(trainingPlan.weekly_total ? { weeklyTotal: trainingPlan.weekly_total } : {}),
  };
}

/**
 * Engine E1.3 — adapt a parsed `wai-plan-v1` JSON plan (from
 * `parsePlanV1Json`) DIRECTLY into `ParsedPlanTable`, so the chat renders
 * the styled table from the JSON even when the coach emits NO markdown
 * table. The deterministic engine treats the JSON as the source of truth;
 * a markdown table becomes an optional fallback. Mirrors `dbToPlanTable`'s
 * field choices (`extractPace`, sentinel start/end/raw, DOW label
 * fallback) so a JSON-rendered table looks identical to a DB-rendered one.
 *
 * `expanded` is passed through as-is — NOT through `expandedDetailsToFields`
 * — because the JSON carries the LLM's `why` alias, which the render layer
 * (`getRenderableExpandedFields`) collapses to the "Why" row; the DB-only
 * `ALLOWED_EXPANDED_KEYS` filter would drop it. Non-string values are
 * filtered at render time, so passing the loosely-validated JSON is safe.
 *
 * Returns null when the plan is missing / has no days.
 */
export function planV1ToPlanTable(plan: PlanV1 | null | undefined): ParsedPlanTable | null {
  if (!plan || !Array.isArray(plan.days) || plan.days.length === 0) return null;

  const rows: PlanRow[] = plan.days
    .slice()
    .sort((a, b) => (a.day_of_week ?? 0) - (b.day_of_week ?? 0))
    .map((d): PlanRow => {
      const expanded: PlanExpandedFields | null =
        d.expanded && typeof d.expanded === 'object' && !Array.isArray(d.expanded)
          ? d.expanded
          : null;
      const pace = extractPace(expanded, d.details);
      const dayLabel =
        (typeof d.day_label === 'string' && d.day_label.trim()) ||
        DOW_TO_LABEL[Math.max(0, Math.min(6, d.day_of_week ?? 0))];
      return {
        day: dayLabel,
        workout: d.workout_type ?? '',
        details: d.details ?? '',
        ...(pace !== undefined ? { pace } : {}),
        expanded,
      };
    });

  const weekHeader =
    (plan.week_title && plan.week_title.trim()) ||
    (typeof plan.week_number === 'number' ? `Week ${plan.week_number}` : undefined) ||
    (plan.date_range && plan.date_range.trim()) ||
    undefined;

  return {
    start: 0,
    end: 0,
    raw: '',
    rows,
    ...(weekHeader ? { weekHeader } : {}),
    ...(plan.weekly_total ? { weeklyTotal: plan.weekly_total } : {}),
  };
}
